import type { ApplicationType } from '@/services/application'

export type BoolString = 'true' | 'false'
export type EmploymentStatus =
  | 'employed'
  | 'selfEmployed'
  | 'trialEmployed'
  | 'hourly'
  | 'retired'
  | 'unemployed'
  | 'student'
  | 'temporarilyEmployed'
export type MaritalStatus = 'single' | 'married' | 'domesticPartner'
export type PropertyType = 'apartment' | 'house' | 'vacation'
export type OtherPropertyType = PropertyType | 'rental'
export type CreateApplicationFormType = ApplicationType
/** The types the shared form registry renders - lånelöfte has its own view and form definition. */
export type GeneralApplicationFormType = Exclude<CreateApplicationFormType, 'commitmentApplication'>
export type RequestedProduct = 'bridgeLoan' | 'depositLoan'
export type OtherLoanType = 'car' | 'student' | 'creditCard' | 'private' | 'guarantee' | 'other'
export type Custody = '0' | '50' | '100'

export const yesNoOptions = [
  { text: 'Ja', value: 'true' },
  { text: 'Nej', value: 'false' },
]

export const custodyOptions = [
  { text: '0 %', value: '0' },
  { text: '50 %', value: '50' },
  { text: '100 %', value: '100' },
]

export type HouseholdChildForm = {
  age: number | undefined
  custody: Custody
  maintenanceSupportIncome: BoolString
  maintenanceSupportCost: BoolString
}

export type HouseholdCarForm = {
  ownershipType: 'owned' | 'leased' | undefined
  monthlyCost: number | undefined
}

export type CapitalAssetForm = {
  type: 'BANK_ACCOUNT' | 'STOCK_SHARES' | 'MUTUAL_FUNDS' | 'OTHER' | undefined
  amount: number | undefined
}

export type ApplicantForm = {
  userId: string
  personalNumber: string
  email: string
  phoneNumber: string
  maritalStatus: MaritalStatus | undefined
  employmentStatus: EmploymentStatus | undefined
  employer: string
  employerOrganisationNumber: string
  employedStartDate: string
  temporaryEmployedEndDate: string
  monthlyIncome: number | undefined
  monthlyIncomeCurrency: 'SEK'
}

export type OtherLoanForm = {
  type: OtherLoanType | undefined
  amount: number | undefined
}

export type OtherPropertyForm = {
  type: OtherPropertyType | undefined
  keepProperty: BoolString | undefined
  loanAmount: number | undefined
  monthlyFee: number | undefined
  ownershipShare: number | undefined
  taxValue: number | undefined
  isPropertyOld: BoolString | undefined
}

export const MAX_BORROWERS = 4
export const MAX_CHILDREN = 10
export const EMPLOYMENT_REQUIRES_EMPLOYER: EmploymentStatus[] = [
  'employed',
  'trialEmployed',
  'hourly',
  'temporarilyEmployed',
]

export const maritalStatusValues: MaritalStatus[] = ['single', 'married', 'domesticPartner']

export const employmentStatusValues: EmploymentStatus[] = [
  'employed',
  'selfEmployed',
  'trialEmployed',
  'hourly',
  'retired',
  'unemployed',
  'student',
  'temporarilyEmployed',
]

export const createApplicationTypeValues: CreateApplicationFormType[] = [
  'newLoanApplication',
  'transferLoanApplication',
  'commitmentApplication',
  'topupLoanApplication',
]

export const requestedProductValues: RequestedProduct[] = ['bridgeLoan', 'depositLoan']

export const carOwnershipTypeValues: NonNullable<HouseholdCarForm['ownershipType']>[] = ['owned', 'leased']

export const capitalAssetTypeValues: NonNullable<CapitalAssetForm['type']>[] = [
  'BANK_ACCOUNT',
  'STOCK_SHARES',
  'MUTUAL_FUNDS',
  'OTHER',
]

export const propertyTypeValues: PropertyType[] = ['apartment', 'house', 'vacation']

export const otherPropertyTypeValues: OtherPropertyType[] = [...propertyTypeValues, 'rental']

export const otherLoanTypeValues: OtherLoanType[] = ['student', 'car', 'creditCard', 'private', 'guarantee', 'other']
